import classNames from 'classnames'
import { uniqueId } from 'lodash'
import * as React from 'react'
import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { SVG_Magnifier } from '../svg/SVG_Magnifier'
import { Spinner } from './Spinner'

type Size = 'small' | 'medium' | 'large'

type Props = {
    deps: CommonDeps
    placeholder: string
    searchTerm: string
    onChange: (searchTerm: string) => void
    size: Size
    disabled?: boolean
    rightNode?: React.ReactNode
    classNames?: string
    onBlur?: () => void
    onFocus?: () => void
    onEnter?: () => void
    innerRef?: React.ForwardedRef<any>
    inputRef?: React.RefObject<HTMLInputElement>
    hideIcon?: boolean
    loading?: boolean
    'data-testid'?: string
}

/**
 * Text input for searching, with magnifier icon and 'clear' button.
 *
 * Comes in three sizes (small, medium, large).
 *
 * See: https://zeroheight.com/96d3cbd9b/p/688b59-search-/b/73fb74 and `SearchBox.stories.tsx`
 */
export class SearchBox extends React.Component<Props, {}> {
    public render = () => renderCatch(this.props.deps, this.tryRender)
    private textInput: React.RefObject<HTMLInputElement>
    private id: string

    constructor(props: Props) {
        super(props)
        this.textInput = this.props.inputRef || React.createRef<HTMLInputElement>()
        // TODO: when migrated to React 18, replace lodash `uniqueId` with `useId` hook
        this.id = uniqueId('SearchBox-')
    }

    focusTextInput() {
        this.textInput.current.focus()
    }

    onKeyPress(event: React.KeyboardEvent) {
        if (event.key === 'Enter' && this.props.onEnter) {
            this.props.onEnter()
        }
    }

    public tryRender = () => {
        return (
            <div
                ref={this.props.innerRef}
                className={classNames(
                    'AppCore__ui__SearchBox',
                    `AppCore__ui__SearchBox__${this.props.size}`,
                    this.props.classNames
                )}
            >
                {this.props.hideIcon !== true && (
                    <label
                        htmlFor={this.id}
                        title={this.props.disabled ? undefined : this.props.placeholder}
                        aria-disabled={this.props.disabled}
                    >
                        <div className={classNames('AppCore__ui__SearchBox__magnifier')}>
                            <SVG_Magnifier />
                        </div>
                    </label>
                )}
                <input
                    id={this.id}
                    data-testid={this.props['data-testid']}
                    ref={this.textInput}
                    type="search"
                    className={classNames('AppCore__ui__SearchBox__input')}
                    placeholder={this.props.placeholder}
                    value={this.props.searchTerm}
                    onChange={(event) => this.props.onChange(event.target.value)}
                    onBlur={this.props.onBlur}
                    onFocus={this.props.onFocus}
                    onKeyPress={this.onKeyPress.bind(this)}
                    disabled={this.props.disabled}
                />
                {this.props.loading && (
                    <div className="AppCore__ui__SearchBox__spinner">
                        <Spinner size={20} />
                    </div>
                )}
                {!this.props.disabled && !this.props.loading && (
                    <button
                        type="button"
                        title="Clear"
                        className={classNames('AppCore__ui__SearchBox__cross')}
                        onClick={() => {
                            this.props.onChange('')
                            this.focusTextInput()
                        }}
                    >
                        {/*
                         * This will not work on without including icons file.
                         * Do not have the time to make this work cross-project right now
                         */}
                        <zaptic-icon icon-name="inverted-cross" />
                    </button>
                )}
                {this.props.rightNode}
            </div>
        )
    }
}
